// project-imports
import { APIAction, handleAPIAction } from './api/apiManager';
import { HubspotAction, handleHubspotAction } from './hubspot/hubspotManager';

// ==============================|| ACTION MANAGER ||============================== //

const handleAction = (action: APIAction | HubspotAction) => {
  return new Promise((resolve, reject) => {
    switch (action.type) {
      case 'api':
        resolve(handleAPIAction(action));
        break;
      case 'hubspot':
        resolve(handleHubspotAction(action));
        break;
      default:
        reject(new Error('[ActionManager] - Unknown Action'));
        break;
    }
  });
};

export default handleAction;

// Helper
function buildHeaders(extra?: Record<string, string>) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-api-key': process.env.REACT_APP_API_KEY || ''
  };

  const accessToken = localStorage.getItem('serviceToken'); // AccessToken
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  return { ...headers, ...(extra || {}) };
}

function buildApiUrl(endpoint: string) {
  const apiUrl = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/v0` : '';
  return apiUrl + endpoint;
}

const parseRetryAfterMs = (retryAfter: string | null) => {
  if (!retryAfter) return null;

  const retryAfterSeconds = Number(retryAfter);
  if (!Number.isNaN(retryAfterSeconds)) {
    return Math.max(0, retryAfterSeconds * 1000);
  }

  const retryAfterDate = new Date(retryAfter).getTime();
  if (!Number.isNaN(retryAfterDate)) {
    return Math.max(0, retryAfterDate - Date.now());
  }

  return null;
};

export class APIRequestError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'APIRequestError';
    this.status = status;
  }
}

export async function postRequestStrict(
  endpoint: string,
  data: any,
  options: {
    successStatus: number;
    successMessage?: string;
    retryStatuses?: number[];
    retries?: number;
    timeoutMs?: number;
  }
): Promise<any> {
  const retryStatuses = options.retryStatuses || [];
  const retries = options.retries ?? 0;
  const timeoutMs = options.timeoutMs ?? 1000;
  const maxAttempts = retries + 1;
  let lastError: APIRequestError | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(data)
      });

      let responseBody: any = null;
      try {
        responseBody = await response.json();
      } catch {
        responseBody = null;
      }

      if (response.status === options.successStatus) {
        if (options.successMessage && responseBody?.message !== options.successMessage) {
          throw new APIRequestError(`Unexpected API response for ${endpoint}`, response.status);
        }

        return responseBody;
      }

      lastError = new APIRequestError(`POST ${endpoint} failed with status ${response.status}`, response.status);

      if (attempt < maxAttempts - 1 && retryStatuses.includes(response.status)) {
        const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'));
        await new Promise((resolve) => setTimeout(resolve, retryAfterMs ?? timeoutMs));
        continue;
      }

      throw lastError;
    } catch (error: any) {
      const apiError =
        error instanceof APIRequestError ? error : new APIRequestError(`POST ${endpoint} failed: ${error?.message || 'Network error'}`);
      lastError = apiError;

      if (attempt < maxAttempts - 1 && apiError.status === undefined) {
        await new Promise((resolve) => setTimeout(resolve, timeoutMs));
        continue;
      }

      throw apiError;
    }
  }

  throw lastError || new APIRequestError(`POST ${endpoint} failed`);
}

// Helpers - Requests
export async function getRequest(endpoint: string): Promise<any> {
  const retries = 3;
  const timeout = 1000;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const url = buildApiUrl(endpoint);
      const response = await fetch(url, {
        method: 'GET',
        // headers: {
        //   'Content-Type': 'application/json',
        //   'x-api-key': process.env.REACT_APP_API_KEY || ''
        // }
        headers: buildHeaders()
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error: any) {
      console.error(`Attempt ${attempt + 1} failed: ${error.message}`);
    }

    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, timeout));
    }
  }

  throw new Error(`GET error after ${retries + 1} attempts`);
}

export async function getBlobRequest(endpoint: string): Promise<Blob> {
  const retries = 3;
  const timeout = 1000;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const url = buildApiUrl(endpoint);
      const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders()
      });

      if (response.ok) {
        const blob = await response.blob();

        if (endpoint.endsWith('/pdf')) {
          const text = await blob.text();
          const normalizedText = text.trim();

          if (normalizedText.startsWith('JVBER')) {
            const binary = window.atob(normalizedText);
            const bytes = new Uint8Array(binary.length);

            for (let index = 0; index < binary.length; index++) {
              bytes[index] = binary.charCodeAt(index);
            }

            return new Blob([bytes], { type: 'application/pdf' });
          }

          if (normalizedText.startsWith('%PDF')) {
            return blob;
          }
        }

        return blob;
      }
    } catch (error: any) {
      console.error(`Attempt ${attempt + 1} failed: ${error.message}`);
    }

    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, timeout));
    }
  }

  throw new Error(`GET blob error after ${retries + 1} attempts`);
}

// export async function postRequest(endpoint: string, data: any): Promise<any> {
//   const retries = 3;
//   const timeout = 1000;

//   for (let attempt = 0; attempt <= retries; attempt++) {
//     try {
//       const apiUrl = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/v0` : '';
//       const url = apiUrl + endpoint;
//       const response = await fetch(url, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           'x-api-key': process.env.REACT_APP_API_KEY || ''
//         },
//         body: JSON.stringify(data)
//       });

//       if (response.ok) {
//         return await response.json();
//       }
//     } catch (error: any) {
//       console.error(`Attempt ${attempt + 1} failed: ${error.message}`);
//     }

//     if (attempt < retries) {
//       // Wait for the specified timeout before the next attempt
//       await new Promise((resolve) => setTimeout(resolve, timeout));
//     }
//   }

//   throw new Error(`POST error after ${retries + 1} attempts`);
// }

export async function postRequest(endpoint: string, data: any, noRetry: boolean = false): Promise<any> {
  const retries = 3;
  const timeout = 1000;
  const maxAttempts = noRetry ? 1 : retries + 1;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const url = buildApiUrl(endpoint);
      const response = await fetch(url, {
        method: 'POST',
        // headers: {
        //   'Content-Type': 'application/json',
        //   'x-api-key': process.env.REACT_APP_API_KEY || ''
        // },
        headers: buildHeaders(),
        body: JSON.stringify(data)
      });

      // if (response.ok) {
      //   return await response.json();
      // }
      if (response.ok) {
        // return await response.json();
        if (response.status === 204) {
          return Promise.resolve(null);
        } else {
          return await response.json();
        }
      }
    } catch (error: any) {
      console.error(`Attempt ${attempt + 1} failed: ${error.message}`);
    }

    if (attempt < maxAttempts - 1) {
      // Wait for the specified timeout before the next attempt
      await new Promise((resolve) => setTimeout(resolve, timeout));
    }
  }

  throw new Error(`POST error after ${maxAttempts} attempts`);
}

export async function putRequest(endpoint: string, data: any): Promise<any> {
  const retries = 3;
  const timeout = 1000;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const url = buildApiUrl(endpoint);
      const response = await fetch(url, {
        method: 'PUT',
        // headers: {
        //   'Content-Type': 'application/json',
        //   'x-api-key': process.env.REACT_APP_API_KEY || ''
        // },
        headers: buildHeaders(),
        body: JSON.stringify(data)
      });

      if (response.ok) {
        if (response.status === 204) {
          return Promise.resolve(null);
        } else {
          return await response.json();
        }
      }
    } catch (error: any) {
      console.error(`Attempt ${attempt + 1} failed: ${error.message}`);
    }

    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, timeout));
    }
  }

  throw new Error(`PUT error after ${retries + 1} attempts`);
}

export async function patchRequest(endpoint: string, data: any): Promise<any> {
  const retries = 3;
  const timeout = 1000;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const url = buildApiUrl(endpoint);
      const response = await fetch(url, {
        method: 'PATCH',
        headers: buildHeaders(),
        body: JSON.stringify(data)
      });

      if (response.ok) {
        if (response.status === 204) {
          return Promise.resolve(null);
        } else {
          return await response.json();
        }
      }
    } catch (error: any) {
      console.error(`Attempt ${attempt + 1} failed: ${error.message}`);
    }

    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, timeout));
    }
  }

  throw new Error(`PATCH error after ${retries + 1} attempts`);
}

export async function deleteRequest(endpoint: string): Promise<any> {
  const retries = 3;
  const timeout = 1000;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/v0` : '';
      const url = apiUrl + endpoint;
      const response = await fetch(url, {
        method: 'DELETE',
        // headers: {
        //   'Content-Type': 'application/json',
        //   'x-api-key': process.env.REACT_APP_API_KEY || ''
        // }
        headers: buildHeaders()
      });

      if (response.ok) {
        if (response.status === 204) {
          return Promise.resolve(null);
        } else {
          return await response.json();
        }
      }
    } catch (error: any) {
      console.error(`Attempt ${attempt + 1} failed: ${error.message}`);
    }

    if (attempt < retries) {
      // Wait for the specified timeout before the next attempt
      await new Promise((resolve) => setTimeout(resolve, timeout));
    }
  }

  throw new Error(`DELETE error after ${retries + 1} attempts`);
}

// export async function putRequest(endpoint: string, data: any): Promise<any> {
//   const retries = 3;
//   const timeout = 1000;

//   for (let attempt = 0; attempt <= retries; attempt++) {
//     try {
//       const apiUrl = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/v0` : '';
//       const url = apiUrl + endpoint;
//       const response = await urlfetch(, {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           'x-api-key': process.env.REACT_APP_API_KEY || ''
//         },
//         body: JSON.stringify(data)
//       });

//       if (response.ok) {
//         return await response.json();
//       }
//     } catch (error: any) {
//       console.error(`Attempt ${attempt + 1} failed: ${error.message}`);
//     }

//     if (attempt < retries) {
//       await new Promise((resolve) => setTimeout(resolve, timeout));
//     }
//   }

//   throw new Error(`PUT error after ${retries + 1} attempts`);
// }

// export async function deleteRequest(endpoint: string): Promise<any> {
//   const retries = 3;
//   const timeout = 1000;

//   for (let attempt = 0; attempt <= retries; attempt++) {
//     try {
//       const apiUrl = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api/v0` : '';
//       const url = apiUrl + endpoint;
//       const response = await urlfetch(, {
//         method: 'DELETE',
//         headers: {
//           'Content-Type': 'application/json',
//           'x-api-key': process.env.REACT_APP_API_KEY || ''
//         }
//       });

//       if (response.ok) {
//         return await response.json();
//       }
//     } catch (error: any) {
//       console.error(`Attempt ${attempt + 1} failed: ${error.message}`);
//     }

//     if (attempt < retries) {
//       await new Promise((resolve) => setTimeout(resolve, timeout));
//     }
//   }

//   throw new Error(`DELETE error after ${retries + 1} attempts`);
// }
